
import React, { useState, useEffect } from 'react';
import { Camera, X, Maximize2, ChevronDown, Sparkles, AlertCircle, Loader2, Key, Download, Plus, RefreshCw, ChevronRight, ChevronLeft, Type as TypeIcon, Image as ImageIcon, ShieldCheck, Info } from 'lucide-react';
import { CreativeBrief, CampaignImage, AppStep } from './types';
import { generateCampaignConcepts, generateTwinImage, setDynamicApiKey } from './services/geminiService';

declare global {
  interface AIStudio {
    hasSelectedApiKey: () => Promise<boolean>;
    openSelectKey: () => Promise<void>;
  }

  interface Window {
    aistudio?: AIStudio;
  }
}

// ... (rest of imports)

const CATEGORY_OPTIONS = ["Tech Review", "Vlog / Lifestyle", "Gaming / High Energy", "Educational / How-to", "Finance / Business", "True Crime / Mystery", "Extreme Challenge"];
const VIBE_OPTIONS = [
  "MrBeast (High Contrast/Action)", 
  "Minimalist Apple-esque", 
  "Cinematic Documentary", 
  "Retro / 80s", 
  "Clean Professional", 
  "Comic Book Style", 
  "High-End Commercial",
  "Environmental Storytelling",
  "Vogue Editorial Style",
  "Cyberpunk Narrative",
  "Anime / Animation Style"
];
const EXPRESSION_OPTIONS = ["Neutral", "Happy / Excited", "Shocked / Wide-Eyed", "Confident / Serious", "Angry / Determined", "Thinking / Curious", "Smirking / Playful"];
const REALISM_OPTIONS = ["Slightly Stylized", "Digital Illustration", "Cinematic Concept Art", "3D Render Style", "Comic Book Art", "Studio Masterpiece"];
const LIGHTING_OPTIONS = [
  "Studio Strobe (Sharp)", 
  "Neon Cyberpunk", 
  "Natural Golden Hour", 
  "Moody Cinematic Noir", 
  "Overhead High-Key", 
  "Rim Lighting (Heroic)", 
  "Softbox Portrait",
  "Golden Hour Portrait",
  "Cinematic Backlight",
  "Product Shot Lighting",
  "Volumetric God Rays",
  "Hard Sun (High Contrast)",
  "Diffused North Light"
];

const PHASE_TABS = [
  { id: 0, label: "Assets", phase: "01" },
  { id: 1, label: "Content", phase: "02" },
  { id: 2, label: "Fidelity", phase: "03" },
  { id: 3, label: "Production", phase: "04" }
];

const PhaseHeader: React.FC<{ phase: string; title: string }> = ({ phase, title }) => (
  <div className="flex flex-col items-center gap-2 mb-12 text-center">
    <span className="text-[10px] font-bold uppercase tracking-[0.5em] text-[#dffe00]">Phase {phase}</span>
    <h2 className="text-2xl md:text-3xl font-display font-semibold text-white tracking-[1px]">{title}</h2>
    <div className="w-12 h-[1px] bg-[#dffe00]/30 mt-4" />
  </div>
);

const StudioDropdown: React.FC<{ label: string; value: string; options: string[]; onChange: (v: string) => void }> = ({ label, value, options, onChange }) => (
  <div className="flex flex-col gap-3 group">
    <label className="text-[9px] font-bold uppercase tracking-widest text-[#dffe00]/50">{label}</label>
    <div className="relative">
      <select 
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-4 px-5 text-[11px] text-white/80 appearance-none focus:outline-none focus:border-[#dffe00]/50 focus:bg-white/[0.06] transition-all cursor-pointer hover:border-white/20"
      >
        {options.map((opt) => <option key={opt} value={opt} className="bg-black">{opt}</option>)}
      </select>
      <div className="absolute right-5 top-1/2 -translate-y-1/2 pointer-events-none text-white/20"><ChevronDown className="w-4 h-4" /></div>
    </div>
  </div>
);

const AssetSlot: React.FC<{ label: string; icon: any; value: string | null; onUpload: (data: string | null) => void }> = ({ label, icon: Icon, value, onUpload }) => (
  <div className="flex flex-col gap-3 flex-1">
    <label className="text-[9px] font-bold uppercase tracking-widest text-center text-[#dffe00]/50">{label}</label>
    <div className="relative aspect-[16/9] bg-white/[0.02] border border-white/5 rounded-2xl overflow-hidden group cursor-pointer hover:border-white/20 transition-all">
      {value ? (
        <>
          <img src={value} className="w-full h-full object-cover" alt={label} />
          <button onClick={(e) => { e.preventDefault(); onUpload(null); }} className="absolute top-2 right-2 p-2 bg-black/60 rounded-full backdrop-blur-md opacity-0 group-hover:opacity-100 transition-opacity"><X className="w-3 h-3" /></button>
        </>
      ) : (
        <label className="w-full h-full flex flex-col items-center justify-center gap-2 text-white/10 group-hover:text-[#dffe00]/40 transition-colors cursor-pointer">
          <Icon className="w-6 h-6 stroke-[1px]" />
          <span className="text-[9px] uppercase tracking-widest">Upload Reference</span>
          <input type="file" className="hidden" accept="image/*" onChange={async e => {
             const file = e.target.files?.[0];
             if (file) {
                const reader = new FileReader();
                reader.readAsDataURL(file);
                reader.onload = (ev) => {
                  const img = new Image();
                  img.onload = () => {
                    const canvas = document.createElement('canvas');
                    let width = img.width;
                    let height = img.height;
                    const maxDim = 1024;
                    if (width > maxDim || height > maxDim) {
                      if (width > height) {
                        height = Math.round((height * maxDim) / width);
                        width = maxDim;
                      } else {
                        width = Math.round((width * maxDim) / height);
                        height = maxDim;
                      }
                    }
                    canvas.width = width;
                    canvas.height = height;
                    const ctx = canvas.getContext('2d');
                    ctx?.drawImage(img, 0, 0, width, height);
                    onUpload(canvas.toDataURL('image/jpeg', 0.8));
                  };
                  img.src = ev.target?.result as string;
                };
             }
          }} />
        </label>
      )}
    </div>
  </div>
);

const App: React.FC = () => {
  const [step, setStep] = useState<AppStep>('landing');
  const [activeTab, setActiveTab] = useState(0);
  const [hasKey, setHasKey] = useState(false);
  const [manualKey, setManualKey] = useState("");
  const [isGenerating, setIsGenerating] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [activeConceptPrompt, setActiveConceptPrompt] = useState<string | null>(null);
  const [appError, setAppError] = useState<string | null>(null);

  const [brief, setBrief] = useState<CreativeBrief>({
    templateImage: null,
    subjectImage: null,
    headline: "NEW VIDEO TITLE",
    vibe: VIBE_OPTIONS[0],
    category: CATEGORY_OPTIONS[0],
    likenessFidelity: "Creative Adaptation",
    expressionIntensity: EXPRESSION_OPTIONS[1],
    realismTuning: REALISM_OPTIONS[0],
    skinStyle: "Smooth Digital",
    lighting: LIGHTING_OPTIONS[0],
    aspectRatio: "16:9",
    numImages: 2,
    imageSize: "1K"
  });

  const [images, setImages] = useState<CampaignImage[]>([]);

  useEffect(() => {
    // Check for baked-in key (AI Studio environment)
    try {
      if (typeof process !== 'undefined' && process.env && process.env.API_KEY) {
        setHasKey(true);
        return;
      }
    } catch (e) {
      // Ignore
    }

    // Check for AI Studio Preview key
    if (window.aistudio?.hasSelectedApiKey) {
      window.aistudio.hasSelectedApiKey().then(setHasKey);
    }
  }, []);

  const handleKeySelection = async () => {
    if (window.aistudio) {
      await window.aistudio.openSelectKey();
      setHasKey(true);
    }
  };

  useEffect(() => {
    if (images.length > 0) {
      setTimeout(() => {
        document.getElementById('output-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  }, [images.length]);

  const handleManualKeySubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (manualKey.trim().length > 10) {
      setDynamicApiKey(manualKey.trim());
      setHasKey(true);
    }
  };

  const generateSingleImage = async (id: string, conceptPrompt: string) => {
    setImages(prev => prev.map(p => p.id === id ? { ...p, status: 'generating' } : p));
    try {
      const url = await generateTwinImage(brief, conceptPrompt);
      setImages(prev => prev.map(p => p.id === id ? { ...p, status: 'completed', imageUrl: url } : p));
    } catch (e: any) {
      if (e.message?.includes("Requested entity was not found") || e.message === "KEY_RESET_REQUIRED") {
        setHasKey(false);
      }
      setImages(prev => prev.map(p => p.id === id ? { ...p, status: 'failed', error: e.message } : p));
    }
  };

  const handleRetry = (id: string) => {
    if (activeConceptPrompt) {
      generateSingleImage(id, activeConceptPrompt);
    }
  };

  const initiateProduction = async () => {
    if (!brief.templateImage || !brief.subjectImage) {
      alert("Upload both a template and a subject photo.");
      setActiveTab(0);
      return;
    }
    
    setIsGenerating(true);
    setAppError(null);
    setImages([]); 

    try {
      const concepts = await generateCampaignConcepts(brief);
      if (!concepts || concepts.length === 0) throw new Error("The creative engine was unable to process your request. Please check your source images.");
      
      const prompt = concepts[0].prompt;
      setActiveConceptPrompt(prompt);

      const initial = Array.from({ length: brief.numImages }, (_, i) => ({
        id: `img-${i}`, themeIndex: 0, status: 'idle' as const
      }));
      setImages(initial);

      await Promise.all(initial.map(img => generateSingleImage(img.id, prompt)));
    } catch (e: any) {
      if (e.message === "KEY_RESET_REQUIRED") {
        setHasKey(false);
      } else {
        setAppError(e.message || "An unexpected error occurred. This is likely due to strict safety filters on high-fidelity images.");
      }
    } finally {
      setIsGenerating(false);
    }
  };

  if (!hasKey) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6 text-white text-center">
        <div className="max-w-md w-full space-y-12">
          <div className="flex flex-col items-center gap-6 animate-fade-in">
            <Key className="w-12 h-12 text-[#dffe00]" />
            <h2 className="font-display font-semibold text-2xl uppercase tracking-[1px]">Studio Activation</h2>
            <p className="text-white/40 text-[10px] uppercase tracking-widest leading-relaxed">
              Unlock ultra-high fidelity rendering by connecting your Gemini 3 API key.
            </p>
            
            {window.aistudio && (
              <button onClick={handleKeySelection} className="w-full py-5 bg-[#dffe00] text-black text-[11px] font-bold uppercase tracking-[0.3em] rounded-full hover:bg-white hover:text-black transition-all">
                Connect Creator Key
              </button>
            )}
            
            <div className="relative flex items-center py-2 w-full">
              <div className="flex-grow border-t border-white/10"></div>
              <span className="flex-shrink-0 mx-4 text-white/30 text-[9px] uppercase tracking-widest">OR ENTER MANUALLY</span>
              <div className="flex-grow border-t border-white/10"></div>
            </div>

            <form onSubmit={handleManualKeySubmit} className="w-full flex flex-col gap-4">
              <input 
                type="password" 
                value={manualKey}
                onChange={(e) => setManualKey(e.target.value)}
                placeholder="Enter Gemini API Key"
                className="w-full bg-white/5 border border-white/10 rounded-xl py-4 px-5 text-center text-white text-sm focus:outline-none focus:border-[#dffe00]"
              />
              <button type="submit" disabled={manualKey.length < 10} className="w-full py-4 bg-white/10 text-white text-[11px] font-bold uppercase tracking-[0.3em] rounded-full hover:bg-white hover:text-black transition-all disabled:opacity-50 disabled:cursor-not-allowed">
                Use Manual Key
              </button>
            </form>

            <a 
              href="https://ai.google.dev/gemini-api/docs/billing" 
              target="_blank" 
              rel="noopener noreferrer"
              className="text-[9px] text-white/20 hover:text-[#dffe00] transition-colors uppercase tracking-widest underline underline-offset-4"
            >
              Billing Documentation
            </a>
          </div>
        </div>
      </div>
    );
  }


  if (step === 'landing') {
    return (
      <div className="min-h-screen bg-black flex flex-col items-center justify-center text-center px-6">
        <div className="relative mb-8">
           <h1 className="text-3xl md:text-5xl font-display font-semibold text-white mb-4 tracking-tighter">AI Twin Studio: <span className="text-[#dffe00]">Thumbnail</span></h1>
        </div>
        <p className="text-white/40 uppercase tracking-[0.5em] text-[10px] mb-12">Gemini 3 Pro Images</p>
        <button onClick={() => setStep('studio')} className="px-12 py-6 border border-white/10 rounded-full text-[11px] font-bold uppercase tracking-[0.3em] hover:bg-[#dffe00] hover:text-black transition-all flex items-center gap-4 group">
          Enter Studio <ChevronRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white pb-32">
      <nav className="fixed top-0 left-0 w-full z-50 px-8 py-6 flex justify-between items-center bg-black/80 backdrop-blur-xl border-b border-white/5">
        <div className="flex items-center gap-3">
          <span className="font-display font-semibold text-[10px] tracking-[1px] uppercase">AI Twin Studio: Thumbnail</span>
        </div>
        <button className="text-[9px] font-bold uppercase tracking-widest text-white/40 hover:text-white" onClick={() => setStep('landing')}>Exit Studio</button>
      </nav>

      <main className="max-w-5xl mx-auto px-6 pt-32">
        <div className="flex justify-center mb-16">
          <div className="flex gap-4 md:gap-12 bg-white/[0.02] border border-white/5 rounded-full px-8 py-3 overflow-x-auto scrollbar-hide">
            {PHASE_TABS.map((tab) => (
              <button key={tab.id} onClick={() => setActiveTab(tab.id)} className={`text-[9px] font-bold uppercase tracking-widest whitespace-nowrap transition-colors ${activeTab === tab.id ? 'text-[#dffe00]' : 'text-white/20 hover:text-white/40'}`}>
                {tab.id + 1}. {tab.label}
              </button>
            ))}
          </div>
        </div>

        <div className="min-h-[500px]">
          {activeTab === 0 && (
            <section className="animate-fade-in flex flex-col items-center">
              <PhaseHeader phase="01" title="High-Res Sources" />
              <div className="w-full flex flex-col md:flex-row gap-8 justify-center mb-16">
                <AssetSlot label="Template Blueprint" icon={ImageIcon} value={brief.templateImage} onUpload={val => setBrief(p => ({ ...p, templateImage: val }))} />
                <AssetSlot label="Subject Reference" icon={Camera} value={brief.subjectImage} onUpload={val => setBrief(p => ({ ...p, subjectImage: val }))} />
              </div>
              <p className="text-[10px] text-white/20 uppercase tracking-widest mb-12 max-w-sm text-center leading-loose">Note: Ensure subject is clearly visible. Safety filters are sensitive to specific facial angles and text.</p>
              <button onClick={() => setActiveTab(1)} className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#dffe00] flex items-center gap-2 hover:text-white transition-colors">
                Content Direction <ChevronRight className="w-4 h-4" />
              </button>
            </section>
          )}

          {activeTab === 1 && (
            <section className="animate-fade-in flex flex-col items-center">
              <PhaseHeader phase="02" title="Creative Protocol" />
              <div className="w-full max-w-lg flex flex-col gap-8 mb-16">
                <div className="flex flex-col gap-3">
                  <label className="text-[9px] font-bold uppercase tracking-widest text-[#dffe00]/50">Master Headline</label>
                  <input 
                    type="text" 
                    value={brief.headline}
                    onChange={e => setBrief(p => ({...p, headline: e.target.value}))}
                    placeholder="E.G. EXPOSING THE TRUTH..."
                    className="w-full bg-white/[0.03] border border-white/10 rounded-xl py-4 px-5 text-white focus:outline-none focus:border-[#dffe00]/50 text-sm font-medium"
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <StudioDropdown label="Production Category" value={brief.category} options={CATEGORY_OPTIONS} onChange={v => setBrief(p => ({...p, category: v}))} />
                  <StudioDropdown label="Visual Vibe" value={brief.vibe} options={VIBE_OPTIONS} onChange={v => setBrief(p => ({...p, vibe: v}))} />
                </div>
              </div>
              <button onClick={() => setActiveTab(2)} className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#dffe00] flex items-center gap-2 hover:text-white transition-colors">
                Likeness Fidelity <ChevronRight className="w-4 h-4" />
              </button>
            </section>
          )}

          {activeTab === 2 && (
            <section className="animate-fade-in flex flex-col items-center w-full">
              <PhaseHeader phase="03" title="Camera Rig" />
              <div className="w-full max-w-4xl mx-auto bg-[#0a0a0a] border border-white/10 rounded-3xl p-8 md:p-12 relative overflow-hidden shadow-2xl mb-16">
                <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-[#dffe00]/30 to-transparent" />
                <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-10">
                  <StudioDropdown label="Likeness Mode" value={brief.likenessFidelity} options={["Artistic Interpretation", "Creative Adaptation", "Stylized Portrait"]} onChange={v => setBrief(p => ({...p, likenessFidelity: v}))} />
                  <StudioDropdown label="Facial Expression" value={brief.expressionIntensity} options={EXPRESSION_OPTIONS} onChange={v => setBrief(p => ({...p, expressionIntensity: v}))} />
                  <StudioDropdown label="Anatomical Precision" value={brief.realismTuning} options={REALISM_OPTIONS} onChange={v => setBrief(p => ({...p, realismTuning: v}))} />
                  <StudioDropdown label="Skin Rendering" value={brief.skinStyle} options={["Smooth Digital", "Soft Editorial", "Painterly Finish", "Cel-Shaded"]} onChange={v => setBrief(p => ({...p, skinStyle: v}))} />
                </div>
              </div>
              <button onClick={() => setActiveTab(3)} className="text-[10px] font-bold uppercase tracking-[0.3em] text-[#dffe00] flex items-center gap-2 hover:text-white transition-colors">
                Final Render <ChevronRight className="w-4 h-4" />
              </button>
            </section>
          )}

          {activeTab === 3 && (
            <section className="animate-fade-in flex flex-col items-center">
              <PhaseHeader phase="04" title="Production Optics" />
              <div className="w-full grid grid-cols-2 gap-8 mb-16 max-w-md">
                <StudioDropdown label="Lighting Rig" value={brief.lighting} options={LIGHTING_OPTIONS} onChange={v => setBrief(p => ({...p, lighting: v}))} />
                <StudioDropdown label="Master Resolution" value={brief.imageSize} options={["1K", "2K", "4K"]} onChange={v => setBrief(p => ({...p, imageSize: v as any}))} />
              </div>
              <div className="flex flex-col items-center gap-6">
                 {appError && (
                   <div className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-2xl flex gap-3 max-w-md animate-fade-in">
                      <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
                      <div className="flex flex-col gap-1">
                        <span className="text-[10px] font-bold text-red-500 uppercase tracking-widest">Protocol Refusal</span>
                        <p className="text-[11px] text-white/70 leading-relaxed">{appError}</p>
                      </div>
                   </div>
                 )}
                 <button onClick={initiateProduction} disabled={isGenerating} className="px-20 py-8 bg-[#dffe00] text-black text-[11px] font-bold uppercase tracking-[0.4em] rounded-full hover:scale-105 transition-all duration-500 disabled:opacity-30 shadow-[0_20px_50px_rgba(223,254,0,0.3)] flex items-center gap-4">
                   {isGenerating ? <><Loader2 className="w-4 h-4 animate-spin" /> {images.length > 0 ? "Rendering Image..." : "Calibrating Rig..."}</> : "Initiate 8K Production"}
                 </button>
                 {isGenerating && <p className="text-[8px] uppercase tracking-[0.4em] text-white/20 animate-pulse">Running advanced photographic synthesis...</p>}
              </div>
            </section>
          )}
        </div>

        {images.length > 0 && (
          <section id="output-section" className="pt-32 border-t border-white/5 mt-32">
             <div className="flex flex-col items-center mb-16">
                <h3 className="text-xl font-display font-semibold mb-2">Master Production Output</h3>
             </div>

             {/* Reference Assets */}
             <div className="flex justify-center gap-8 mb-16">
               {brief.templateImage && (
                 <div className="flex flex-col gap-3 items-center">
                   <span className="text-[8px] uppercase tracking-widest text-white/40">Layout Reference</span>
                   <div className="w-40 aspect-[16/9] rounded-xl overflow-hidden border border-white/10 opacity-60 hover:opacity-100 transition-opacity">
                     <img src={brief.templateImage} className="w-full h-full object-cover" alt="Layout" />
                   </div>
                 </div>
               )}
               {brief.subjectImage && (
                 <div className="flex flex-col gap-3 items-center">
                   <span className="text-[8px] uppercase tracking-widest text-white/40">Style Reference</span>
                   <div className="w-40 aspect-[16/9] rounded-xl overflow-hidden border border-white/10 opacity-60 hover:opacity-100 transition-opacity">
                     <img src={brief.subjectImage} className="w-full h-full object-cover" alt="Subject" />
                   </div>
                 </div>
               )}
             </div>

             <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                {images.map(img => (
                  <div key={img.id} className="bg-white/[0.02] border border-white/5 rounded-3xl overflow-hidden group relative shadow-2xl transition-all hover:border-[#dffe00]/30">
                    <div className="aspect-[16/9] relative overflow-hidden">
                      {img.status === 'completed' ? (
                        <>
                          <img src={img.imageUrl} className="w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105" alt="Output" />
                          <div className="absolute inset-0 bg-black/80 opacity-0 group-hover:opacity-100 transition-all flex items-center justify-center gap-6 backdrop-blur-sm">
                            <button onClick={() => setPreviewImage(img.imageUrl!)} className="p-4 bg-white text-black rounded-full hover:scale-110 transition-transform"><Maximize2 className="w-5 h-5" /></button>
                            <button onClick={() => { const link = document.createElement('a'); link.href = img.imageUrl!; link.download = 'fidel-thumbnail.png'; link.click(); }} className="p-4 bg-white text-black rounded-full hover:scale-110 transition-transform"><Download className="w-5 h-5" /></button>
                          </div>
                        </>
                      ) : (
                        <div className="flex flex-col items-center justify-center h-full gap-6 px-12 text-center">
                           {img.status === 'failed' ? (
                             <>
                               <AlertCircle className="w-10 h-10 text-red-500/50" />
                               <p className="text-[10px] text-white/30 uppercase tracking-widest leading-relaxed">
                                  {img.error}
                               </p>
                               <button onClick={() => handleRetry(img.id)} className="mt-4 p-3 bg-white/5 border border-white/10 rounded-full hover:bg-white/10 transition-all"><RefreshCw className="w-4 h-4" /></button>
                             </>
                           ) : (
                             <>
                               <div className="relative">
                                 <Loader2 className="w-12 h-12 text-[#dffe00] animate-spin opacity-50" />
                                 <Sparkles className="w-5 h-5 text-[#dffe00] absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 animate-pulse" />
                               </div>
                               <div className="flex flex-col gap-2">
                                 <span className="text-[9px] uppercase tracking-[0.4em] text-[#dffe00]/60">Developing...</span>
                                 <span className="text-[7px] uppercase tracking-widest text-white/20">Synthesizing Pixels</span>
                               </div>
                             </>
                           )}
                        </div>
                      )}
                    </div>
                  </div>
                ))}
             </div>

             {/* Director's Prompt */}
             {activeConceptPrompt && (
               <div className="mt-20 max-w-4xl mx-auto bg-white/[0.02] border border-white/5 rounded-3xl p-8 md:p-10">
                 <h4 className="text-[10px] uppercase tracking-[0.3em] text-[#dffe00] mb-6 flex items-center gap-3">
                   <TypeIcon className="w-4 h-4" /> Director's Prompt
                 </h4>
                 <p className="text-sm text-white/60 leading-relaxed font-mono">
                   {activeConceptPrompt}
                 </p>
               </div>
             )}
          </section>
        )}
      </main>

      {/* Image Preview Modal */}
      {previewImage && (
        <div className="fixed inset-0 z-[100] bg-black/98 flex items-center justify-center p-8 backdrop-blur-2xl" onClick={() => setPreviewImage(null)}>
           <img src={previewImage} className="max-w-full max-h-full object-contain rounded-xl shadow-[0_0_100px_rgba(223,254,0,0.2)] border border-white/10" alt="Preview" />
        </div>
      )}

      {/* Footer */}
      <footer className="fixed bottom-0 left-0 w-full p-6 flex justify-center z-40 pointer-events-none">
        <button 
          onClick={() => setHasKey(false)} 
          className="pointer-events-auto flex items-center gap-2 px-4 py-2 bg-white/5 hover:bg-white/10 border border-white/10 rounded-full text-[9px] font-bold uppercase tracking-widest text-white/50 hover:text-white transition-all backdrop-blur-md"
        >
          <Key className="w-3 h-3" />
          Update API Key
        </button>
      </footer>
    </div>
  );
};

export default App;
